import { TextField, IconButton, Grid } from '@material-ui/core';
import React, { useEffect, useReducer } from 'react';
import { useTranslation } from 'react-i18next';
import DeleteIcon from '@material-ui/icons/Delete';
import { IDynamicFormExtenderChildren } from '../DynamicFormExtender';
import { FormInternationalPrice } from './FormIntPrice';
import { WatchFunction } from '../interfaces/IDynamicFormRow';
import { ITaxType } from '../../interfaces/ITaxes';
import {
    PricingOptionsReducer,
    setPricingOption,
    setTaxItems,
    setTaxPrice,
} from './PricingOptionsState/PricingOptionsReducer';

interface IPricingOptionProps extends IDynamicFormExtenderChildren {
    option_discount?: number;
    hide_exclusive?: boolean;
    option_name: string;
    option_price: number;
    option_price_exclusive: number;
    watch?: WatchFunction;
}

const PricingOption = ({
    index,
    option_name,
    hide_exclusive,
    watch,
    option_price,
    onChangeHandler,
    onDeleteHandler,
    option_discount,
    option_price_exclusive,
    disabled,
}: IPricingOptionProps): JSX.Element => {
    const { t } = useTranslation();
    const [state, dispatch] = useReducer(PricingOptionsReducer, {
        ...generateInitalPriceState(option_price, option_price_exclusive),
        taxes: undefined,
        taxType: undefined,
    });
    const { taxType } = state;
    /**Watches if the value changes */
    const taxItems = watch!('item_taxes');
    useEffect(() => {
        if (taxItems && Array.isArray(taxItems)) {
            dispatch(setTaxItems(taxItems));
        }
    }, [taxItems]);

    useEffect(() => {
        if (state.option_price === 0 || state.option_price) {
            onChangeHandler(index, 'option_price', state.option_price);
        }
        if (state.option_price_exclusive === 0 || state.option_price_exclusive) {
            onChangeHandler(index, 'option_price_exclusive', state.option_price_exclusive);
        }
        // eslint-disable-next-line
    }, [state.option_price, state.option_price_exclusive]);

    const onChange = (ev: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const key = ev.target.name;
        let value = ev.target.value;
        onChangeHandler(index, key, value);
    };
    function onChangePrice(value: number, maskedValue: string) {
        dispatch(setPricingOption(value));
    }
    function onChangeTaxPrice(value: number, maskedValue: string) {
        dispatch(setTaxPrice(value));
    }
    return (
        <>
            <Grid item xs={3} style={{ margin: '0 -4px' }}>
                <TextField
                    variant="outlined"
                    data-component-name={'OPTION_NAME'}
                    style={{ padding: '5px' }}
                    disabled={disabled}
                    name="option_name"
                    value={option_name || ''}
                    label={t('menu.priceoptions.opname')}
                    onChange={onChange}
                    fullWidth
                />
            </Grid>
            <Grid item xs={3}>
                <FormInternationalPrice
                    disabled={disabled || taxType === ITaxType.ADDITIVE}
                    defaultValue={state.option_price ? state.option_price : undefined}
                    label={t('menu.priceoptions.opprice')}
                    onChange={onChangePrice}
                />
            </Grid>
            <Grid item xs={3}>
                <FormInternationalPrice
                    hidden={hide_exclusive}
                    disabled={disabled || taxType === ITaxType.INCLUSIVE}
                    defaultValue={state.option_price_exclusive ? state.option_price_exclusive : undefined}
                    label={t('menu.priceoptions.oppricetax')}
                    onChange={onChangeTaxPrice}
                />
            </Grid>
            <Grid item xs={2}>
                <TextField
                    style={{ padding: '5px' }}
                    InputProps={{ inputProps: { min: 0, max: 100 } }}
                    type="number"
                    name="option_discount"
                    disabled={disabled}
                    value={option_discount?.toString()}
                    label={t('menu.priceoptions.discount')}
                    onChange={onChange}
                    variant={'outlined'}
                />
            </Grid>
            <Grid item xs={1} style={{ display: 'flex', flexDirection: 'column-reverse' }}>
                {!disabled && (
                    <IconButton onClick={() => onDeleteHandler(index)} aria-label="delete" color="secondary">
                        <DeleteIcon />
                    </IconButton>
                )}
            </Grid>
        </>
    );
};

function generateInitalPriceState(option_price: number, option_price_exclusive: number) {
    const option = option_price || 0;
    const e_option = option_price_exclusive || option;
    return {
        option_price: option,
        option_price_exclusive: e_option,
    };
}

export default PricingOption;
